import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFieldArray, useFormContext as useReactHookFormContext } from 'react-hook-form'
import { Eye, EyeOff, Loader2, Upload, X } from 'lucide-react'

import { Input } from '@/shared/components/ui/input'
import { Textarea } from '@/shared/components/ui/textarea'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { Switch } from '@/shared/components/ui/switch'
import { DatePicker } from '@/shared/components/ui/date-picker'
import { TimeSelect } from '@/shared/components/ui/time-select'
import { PhoneInput } from '@/shared/components/ui/phone-input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Label } from '@/shared/components/ui/label'
import { MultiSelectDropdown } from '@/shared/components/ui/multi-select-dropdown'
import { AttributeRichText } from '@/shared/components/ui/attribute-rich-text'
import { Button } from '@/shared/components/ui/button'
import { CurrencyInput } from '@/shared/components/ui/currency-input'
import { AddressCapture } from '@/shared/components/ui/address-capture'
import {
  toAddressInputValue,
  toMailingAddress,
  isMeaningfulMailingAddress,
  type MailingAddress,
} from '@/shared/lib/formatting/address'
import { ResourceSearchInput, type ResourceOption } from '@/shared/components/ui/resource-search-input'
import { SearchableSelect } from '@/shared/components/ui/searchable-select'
import { useResourceLookup } from '@/shared/components/ui/resource-lookup-context'
import { ExampleGallery } from '@/shared/components/example-gallery'
import { FileUploader } from '@/shared/components/ui/file-uploader'
import {
  humanizeAccept,
  getAcceptForUseCase,
  toUploadedFileItem,
  type UploadedFileItem,
} from '@/shared/lib/file-types'
import { cn, capitalizeFirstLetter } from '@/shared/lib/utils'
import { compressImage } from '@/shared/lib/compress-image'
import { resolveDependentOptions, isFieldVisible, isConditionallyRequired } from './normalise'
import { useFormContext, useFieldState, useUpload } from './form-context'
import type { FormField, RenderInputContext } from './generated-types'

function toSnakeCase(value: string): string {
  return value.replace(/[\s-]/g, '_').toLowerCase()
}

function getFieldName(field: FormField): string {
  if ('name' in field && field.name) return field.name
  if ('label' in field && field.label) return toSnakeCase(field.label)
  throw new Error("FormField must have either a 'name' or 'label' property")
}

function getFieldLabel(field: FormField): string {
  return ('label' in field && field.label) || ('name' in field && field.name) || ''
}

function getFieldTestId(field: FormField): string | undefined {
  return field.testId
}

function renderFieldLabel(label: string, field: FormField): React.ReactNode {
  if (!field.required) return label

  return (
    <span className="inline-flex items-center gap-[2px]">
      <span>{label}</span>
      <span
        aria-hidden="true"
        className="mt-2 text-[17px] leading-[4px] text-[var(--status-error)]"
      >
        *
      </span>
    </span>
  )
}

type GeneratedInputProps = {
  field: FormField
  index: number
  context?: RenderInputContext
}

function FieldShell({
  field,
  children,
  error,
  footer,
}: {
  field: FormField
  children: React.ReactNode
  error?: string
  footer?: React.ReactNode
}) {
  const fieldName = getFieldName(field)
  const label = getFieldLabel(field)

  return (
    <div className={field.className}>
      {field.type !== 'checkbox' && label && (
        <Label
          htmlFor={fieldName}
          className={cn(
            'mb-1.5 block clara-label font-normal',
            field.labelClassName,
            error && 'text-[var(--status-error)]',
          )}
        >
          {renderFieldLabel(label, field)}
        </Label>
      )}
      {children}
      {footer !== undefined
        ? footer
        : error
          ? <p className="mt-1 text-[11px] text-[var(--status-error)]">{error}</p>
          : field.hint
            ? <p className="mt-1 text-[11px] text-[var(--ink-3)]">{field.hint}</p>
            : null}
    </div>
  )
}

function TextField({
  field,
  inputType = 'text',
  autoCapitalize = false,
}: {
  field: FormField
  inputType?: string
  autoCapitalize?: boolean
}) {
  const name = getFieldName(field)
  const testId = getFieldTestId(field)
  const formContext = useFormContext()
  const { value, error, touched, setValue, clearError, markTouched } = useFieldState(name)
  const [showPassword, setShowPassword] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const cursorRestoreRef = useRef<number | null>(null)
  const isPasswordField = inputType === 'password'
  const hasLeadingIcon = Boolean(field.leadingIcon)
  const resolvedInputType = isPasswordField && showPassword ? 'text' : inputType
  const hasError = touched && Boolean(error)
  const shouldCapitalize = autoCapitalize && field.autoCapitalize !== false

  // Restore the caret after capitalisation rewrites the first character so
  // the cursor doesn't jump to the end mid-edit. Mirrors CurrencyInput.
  useEffect(() => {
    if (cursorRestoreRef.current !== null && inputRef.current) {
      const pos = cursorRestoreRef.current
      try {
        inputRef.current.setSelectionRange(pos, pos)
      } catch {
        // setSelectionRange throws on some input types (e.g. email, number)
        // in Chrome/Safari; the caret restore is a nice-to-have, not critical.
      }
      cursorRestoreRef.current = null
    }
  })

  return (
    <FieldShell field={field} error={hasError ? error : undefined}>
      <div className="relative">
        {field.leadingIcon ? (
          <span className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-[var(--ink-3)]">
            {field.leadingIcon}
          </span>
        ) : null}
        <Input
          ref={inputRef}
          data-testid={testId}
          id={name}
          type={resolvedInputType}
          value={(value as string) ?? ''}
          placeholder={field.placeholder}
          disabled={field.disabled}
          autoFocus={field.autoFocus}
          autoComplete={field.autoComplete}
          maxLength={field.maxLength}
          inputMode={field.inputMode}
          aria-invalid={hasError || undefined}
          className={cn(
            hasLeadingIcon && 'pl-10',
            isPasswordField && 'pr-10',
            // Keep the revealed password the same size as the masked dots so
            // toggling the eye doesn't shrink/jump the field. The base Input
            // adds the bold + letter-spacing prominence only while masked; the
            // revealed real characters read better at normal weight and spacing.
            isPasswordField && showPassword && 'text-lg',
            field.inputClassName,
          )}
          onChange={(event) => {
            const raw = event.target.value
            const next = shouldCapitalize ? capitalizeFirstLetter(raw) : raw
            if (next !== raw) cursorRestoreRef.current = event.target.selectionStart
            setValue(next)
            clearError()
            field.onChange?.(next, formContext)
          }}
          onBlur={(event) => {
            markTouched()
            field.onBlur?.(event.target.value, formContext)
          }}
        />
        {isPasswordField ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            aria-pressed={showPassword}
            onPointerDown={(event) => event.preventDefault()}
            onClick={() => setShowPassword((v) => !v)}
            className="absolute right-3 top-1/2 z-10 h-6 w-6 -translate-y-1/2 cursor-pointer rounded-md text-[var(--ink-3)] hover:bg-transparent"
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
        ) : null}
      </div>
    </FieldShell>
  )
}

function PhoneField({ field }: { field: FormField }) {
  const name = getFieldName(field)
  const formContext = useFormContext()
  const { value, error, touched, setValue, clearError, markTouched } = useFieldState(name)
  const hasError = touched && Boolean(error)

  return (
    <FieldShell field={field} error={hasError ? error : undefined}>
      <PhoneInput
        id={name}
        value={(value as string) ?? ''}
        onChange={(next) => {
          setValue(next)
          clearError()
          field.onChange?.(next, formContext)
        }}
        onBlur={() => {
          markTouched()
          field.onBlur?.(value, formContext)
        }}
        disabled={field.disabled}
        placeholder={field.placeholder}
        hasError={hasError}
      />
    </FieldShell>
  )
}

/** A settings-style on/off row: label and `hint` on the left, the switch on the right. */
function SwitchField({ field }: { field: FormField }) {
  const name = getFieldName(field)
  const testId = getFieldTestId(field)
  const formContext = useFormContext()
  const { value, error, touched, setValue, clearError, markTouched } = useFieldState(name)
  const label = getFieldLabel(field)
  const hasError = touched && Boolean(error)
  const description = field.describe ? field.describe(formContext) : field.hint

  return (
    <div className={field.className}>
      <div className="flex items-center justify-between gap-6">
        <div>
          <Label htmlFor={name} className="text-[14px] text-[var(--ink)]">
            {renderFieldLabel(label, field)}
          </Label>
          {description && (
            <p className="mt-0.5 text-[12px] text-[var(--ink-3)]">{description}</p>
          )}
        </div>
        <Switch
          id={name}
          data-testid={testId}
          aria-invalid={hasError || undefined}
          checked={Boolean(value)}
          disabled={field.disabled}
          onCheckedChange={(next) => {
            setValue(next)
            clearError()
            markTouched()
            field.onChange?.(next, formContext)
          }}
        />
      </div>
      {hasError && (
        <p className="mt-1 text-[11px] text-[var(--status-error)]">{error}</p>
      )}
    </div>
  )
}

function CheckboxField({ field }: { field: FormField }) {
  const name = getFieldName(field)
  const testId = getFieldTestId(field)
  const formContext = useFormContext()
  const { value, error, touched, setValue, clearError, markTouched } = useFieldState(name)
  const label = getFieldLabel(field)
  const hasError = touched && Boolean(error)

  return (
    <div className={field.className}>
      <Label className="flex cursor-pointer items-start gap-2.5 font-normal">
        <Checkbox
          data-testid={testId}
          aria-invalid={hasError || undefined}
          checked={Boolean(value)}
          onCheckedChange={(next) => {
            setValue(Boolean(next))
            clearError()
            // A click is the whole interaction, so an error set below shows now.
            markTouched()
            field.onChange?.(Boolean(next), formContext)
          }}
        />
        <span className="text-[13px] text-[var(--ink-2)]">{renderFieldLabel(label, field)}</span>
      </Label>
      {hasError && (
        <p className="mt-1 text-[11px] text-[var(--status-error)]">{error}</p>
      )}
    </div>
  )
}

/**
 * A BOOLEAN attribute whose `configuration_data.display_as === 'disclaimer'`.
 * Renders a single unified component - a tenant-authored notice (title + long
 * copy) followed by the acknowledgement checkbox - so the disclaimer and its
 * confirmation live together wherever the attribute is captured, instead of
 * being split across a separate copy-only panel and a bare checkbox.
 *
 * Copy is config-driven (never hardcoded): `disclaimer_title` /
 * `disclaimer_text` come from `configuration_data`, and the checkbox label is
 * the attribute's `display_name` (the field label). Validation matches a
 * required checkbox (`=== true`) via `getFormValidation`'s `case 'disclaimer'`.
 */
function DisclaimerField({ field }: { field: FormField }) {
  const name = getFieldName(field)
  const testId = getFieldTestId(field)
  const formContext = useFormContext()
  const { value, error, touched, setValue, clearError } = useFieldState(name)
  const label = getFieldLabel(field)
  const hasError = touched && Boolean(error)

  const config = (field.configurationData ?? {}) as Record<string, unknown>
  const title = typeof config.disclaimer_title === 'string' ? config.disclaimer_title : ''
  const text =
    typeof config.disclaimer_text === 'string'
      ? config.disclaimer_text
      : typeof field.hint === 'string'
        ? field.hint
        : ''
  // Optional Markdown checkbox label (links/emphasis) authored in config; the
  // plain field label is the fallback. See AttributeRichText for the contract.
  const checkboxLabel =
    typeof config.checkbox_label === 'string' && config.checkbox_label.trim() !== ''
      ? config.checkbox_label
      : null

  return (
    <div className={field.className}>
      <div
        className={cn(
          'rounded-[8px] border-[0.5px] p-4',
          hasError ? 'border-[var(--status-error)]' : 'border-[var(--rule)]',
        )}
      >
        {title && (
          <p className="m-0 mb-1.5 text-[12px] font-medium text-[var(--ink-1)]">{title}</p>
        )}
        {text && (
          <AttributeRichText
            content={text}
            className="m-0 text-[12px] leading-[1.6] text-[var(--ink-2)]"
          />
        )}
        <Label className="mt-3 flex cursor-pointer items-start gap-2 font-normal">
          <Checkbox
            data-testid={testId}
            aria-invalid={hasError || undefined}
            aria-required={field.required ? true : undefined}
            checked={Boolean(value)}
            className={cn('mt-0.5', hasError && 'border-[var(--status-error)]')}
            onCheckedChange={(next) => {
              setValue(Boolean(next))
              clearError()
              field.onChange?.(Boolean(next), formContext)
            }}
          />
          <span className="text-[12px] leading-[1.5] text-[var(--ink-1)]">
            {checkboxLabel ? (
              <>
                <AttributeRichText content={checkboxLabel} variant="inline" />
                {field.required && (
                  <span
                    aria-hidden="true"
                    className="ml-0.5 text-[var(--status-error)]"
                  >
                    *
                  </span>
                )}
              </>
            ) : (
              renderFieldLabel(label, field)
            )}
          </span>
        </Label>
      </div>
      {hasError && <p className="mt-1 text-[11px] text-[var(--status-error)]">{error}</p>}
    </div>
  )
}

/**
 * Small finding chip rendered after a checklist-style option label when the
 * option carries a `severity`. Red maps to the error token, yellow/amber to
 * the warn token; `none`/absent renders nothing. Tokens only - no inline
 * styles, no Tailwind default colours.
 */
function OptionSeverityChip({
  severity,
}: {
  severity?: 'red' | 'yellow' | 'none'
}) {
  if (severity !== 'red' && severity !== 'yellow') return null
  const isRed = severity === 'red'
  return (
    <span
      className={cn(
        'ml-auto inline-flex h-[18px] shrink-0 items-center rounded-full px-2 text-[10px] font-medium uppercase tracking-[0.04em]',
        isRed
          ? 'bg-[var(--status-error-bg)] text-[var(--status-error)]'
          : 'bg-[var(--status-warn-bg)] text-[var(--status-warn)]',
      )}
    >
      {isRed ? 'Red' : 'Amber'}
    </span>
  )
}

function FreeformListField({
  field,
  selected,
  commit,
  error,
  testId,
}: {
  field: FormField
  selected: string[]
  commit: (next: string[]) => void
  error?: string
  testId?: string
}) {
  const [draft, setDraft] = useState('')

  function addDraft() {
    const next = draft.trim()
    if (!next) return
    if (!selected.includes(next)) commit([...selected, next])
    setDraft('')
  }

  return (
    <FieldShell field={field} error={error}>
      <div className="flex flex-col gap-1.5">
        <div className="flex min-h-[38px] cursor-text flex-wrap items-center gap-1.5 rounded-[4px] border-[0.5px] border-[var(--rule-strong)] bg-transparent px-3 py-2">
          {selected.map((item) => (
            <span
              key={item}
              className="inline-flex items-center gap-1 rounded-full bg-[var(--bg-subtle)] px-2 py-0.5 text-[11px] text-[var(--ink-2)]"
            >
              {item}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => commit(selected.filter((value) => value !== item))}
                className="h-auto w-auto cursor-pointer p-0 text-[var(--ink-3)]"
              >
                <X className="h-2.5 w-2.5" />
              </Button>
            </span>
          ))}
          <Input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ',') {
                event.preventDefault()
                addDraft()
              }
              if (event.key === 'Backspace' && !draft && selected.length > 0) {
                commit(selected.slice(0, -1))
              }
            }}
            onBlur={addDraft}
            placeholder={
              selected.length === 0
                ? field.placeholder || 'Type a value and press Enter'
                : ''
            }
            className="h-auto min-w-[120px] flex-1 border-0 p-0 shadow-none focus-visible:ring-0"
            data-testid={testId}
          />
        </div>
      </div>
    </FieldShell>
  )
}

function SelectField({ field }: { field: FormField }) {
  const name = getFieldName(field)
  const testId = getFieldTestId(field)
  const formContext = useFormContext()
  const { value, error, touched, setValue, clearError } = useFieldState(name)

  // Cascading options: resolve against live values every render so any
  // ancestor change re-cascades.
  const configurationData = field.configurationData
  const dependsOn = configurationData?.depends_on as string | undefined
  const dynamicOptions = dependsOn
    ? (resolveDependentOptions(configurationData, formContext.values) ?? null)
    : null
  const options = dynamicOptions ?? field.options ?? []

  // Drop the saved value when it's no longer a valid option after a parent
  // change.
  useEffect(() => {
    if (!dependsOn) return
    if (value == null || value === '') return
    if (Array.isArray(value)) {
      const valid = value.filter((v) => options.some((opt) => opt.value === v))
      if (valid.length !== value.length) setValue(valid)
      return
    }
    if (!options.some((opt) => opt.value === value)) {
      setValue('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependsOn, options, value])

  if (field.isMulti || field.type === 'multiSelect') {
    const selected = Array.isArray(value) ? (value as string[]) : []
    const allowSelectAll = configurationData?.allow_select_all === true
    const commit = (next: string[]) => {
      setValue(next)
      clearError()
      field.onChange?.(next, formContext)
    }
    // Checklist-style multi-selects (report findings with per-option severity)
    // stay an inline list so their severity chips render. Every other
    // multi-select - including long reference-data lists like geo-zones -
    // collapses to a compact searchable dropdown instead of a wall of
    // checkboxes.
    const hasSeverity = options.some((option) => option.severity)
    // No preset options: each record types its own list (Enter / comma).
    if (!hasSeverity && options.length === 0) {
      return (
        <FreeformListField
          field={field}
          selected={selected}
          commit={commit}
          error={touched ? error : undefined}
          testId={testId}
        />
      )
    }
    if (!hasSeverity) {
      return (
        <FieldShell field={field} error={touched ? error : undefined}>
          <MultiSelectDropdown
            value={selected}
            onChange={commit}
            options={options.map((option) => ({
              value: option.value,
              label: option.label,
            }))}
            allowSelectAll={allowSelectAll}
            placeholder={
              field.placeholder ||
              `Select ${getFieldLabel(field).toLowerCase()}`
            }
            searchPlaceholder={`Search ${getFieldLabel(field).toLowerCase()}...`}
            disabled={field.disabled}
            data-testid={testId}
          />
        </FieldShell>
      )
    }
    const optionValues = options.map((option) => option.value)
    const allSelected =
      optionValues.length > 0 && optionValues.every((v) => selected.includes(v))
    return (
      <FieldShell field={field} error={touched ? error : undefined}>
        <div className="flex flex-col gap-2 rounded-[8px] border border-input p-3">
          {allowSelectAll && optionValues.length > 0 && (
            <Label className="flex cursor-pointer items-center gap-2.5 border-b-[0.5px] border-b-[var(--rule)] pb-2 font-normal">
              <Checkbox
                checked={allSelected}
                onCheckedChange={() => commit(allSelected ? [] : optionValues)}
              />
              <span className="text-[13px] font-medium text-[var(--ink-2)]">
                Select all
              </span>
            </Label>
          )}
          {options.map((option) => {
            const isChecked = selected.includes(option.value)
            return (
              <Label key={option.value} className="flex cursor-pointer items-center gap-2.5 font-normal">
                <Checkbox
                  checked={isChecked}
                  onCheckedChange={() =>
                    commit(
                      isChecked
                        ? selected.filter((v) => v !== option.value)
                        : [...selected, option.value],
                    )
                  }
                />
                <span className="text-[13px] text-[var(--ink-2)]">{option.label}</span>
                <OptionSeverityChip severity={option.severity} />
              </Label>
            )
          })}
        </div>
      </FieldShell>
    )
  }

  const currentValue = (value as string) ?? ''
  const displayValue =
    currentValue && options.some((opt) => opt.value === currentValue) ? currentValue : ''

  // A searchable combobox instead of a plain dropdown when the field opts in
  // via `configuration_data.searchable`. Same value contract as the plain
  // SELECT; opt-in so existing selects are unchanged.
  const searchable = field.configurationData?.['searchable'] === true
  if (searchable) {
    return (
      <FieldShell field={field} error={touched ? error : undefined}>
        <SearchableSelect
          id={name}
          value={displayValue}
          onValueChange={(next) => {
            setValue(next)
            clearError()
            field.onChange?.(next, formContext)
          }}
          groups={[
            {
              label: '',
              options: options.map((o) => ({ value: o.value, label: o.label })),
            },
          ]}
          placeholder={field.placeholder || `Select ${getFieldLabel(field).toLowerCase()}`}
          triggerClassName={cn('w-full', field.inputClassName)}
          data-testid={testId}
        />
      </FieldShell>
    )
  }

  return (
    <FieldShell field={field} error={touched ? error : undefined}>
      <Select
        value={displayValue}
        // A `select` field declaring `disabled` was rendered fully
        // interactive: the flag reached no element, so the control looked
        // locked in the config and was not. Every other generated input
        // already honoured it.
        disabled={field.disabled}
        onValueChange={(next) => {
          setValue(next)
          clearError()
          field.onChange?.(next, formContext)
        }}
      >
        <SelectTrigger id={name} data-testid={testId} className={cn('w-full', field.inputClassName)}>
          <SelectValue placeholder={field.placeholder || `Select ${getFieldLabel(field).toLowerCase()}`} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.severity === 'red' || option.severity === 'yellow' ? (
                <span className="flex w-full items-center gap-2">
                  <span>{option.label}</span>
                  <OptionSeverityChip severity={option.severity} />
                </span>
              ) : (
                option.label
              )}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FieldShell>
  )
}

function DateField({ field }: { field: FormField }) {
  const testId = getFieldTestId(field)
  const name = getFieldName(field)
  const formContext = useFormContext()
  const { value, error, touched, setValue, clearError } = useFieldState(name)

  return (
    <FieldShell field={field} error={touched ? error : undefined}>
      <DatePicker
        data-testid={testId}
        value={(value as string) ?? ''}
        onChange={(next) => {
          setValue(next)
          clearError()
          field.onChange?.(next, formContext)
        }}
        placeholder={field.placeholder}
        className={field.inputClassName}
        disablePast={field.disablePast}
        disableFuture={field.disableFuture}
      />
    </FieldShell>
  )
}

const pad2 = (n: number) => String(n).padStart(2, '0')

/** Decode a stored ISO datetime into a local YYYY-MM-DD date part. */
function decodeLocalDate(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

/** Decode a stored ISO datetime into a local HH:MM time part. */
function decodeLocalTime(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`
}

/**
 * DateTimeField - a date picker + 15-minute time picker that together
 * capture a full timestamp. The two halves are held locally so a partial
 * pick (date chosen, time not yet) is never lost; the combined value is
 * written to the form as a UTC ISO-8601 string (matching the
 * ``parse: datetime`` overwrite on the backend) only once both halves are
 * set. The local time the user picks is interpreted in their timezone and
 * converted to UTC, mirroring the inspection create wizard.
 */
function DateTimeField({ field }: { field: FormField }) {
  const testId = getFieldTestId(field)
  const name = getFieldName(field)
  const label = getFieldLabel(field)
  const formContext = useFormContext()
  const { value, error, touched, setValue, clearError } = useFieldState(name)

  const iso = (value as string) ?? ''
  const [datePart, setDatePart] = useState(() => decodeLocalDate(iso))
  const [timePart, setTimePart] = useState(() => decodeLocalTime(iso))

  const commit = (nextDate: string, nextTime: string) => {
    setDatePart(nextDate)
    setTimePart(nextTime)
    if (nextDate && nextTime) {
      // Build the local datetime from explicit parts rather than parsing an
      // interpolated string - the string form (`YYYY-MM-DDTHH:mm:00`) is subject
      // to browser-specific parsing quirks/timezone drift, whereas the numeric
      // constructor is unambiguously local time.
      const [year, month, day] = nextDate.split('-').map(Number)
      const [hours, minutes] = nextTime.split(':').map(Number)
      const combined = new Date(year, month - 1, day, hours, minutes)
      const nextIso = Number.isNaN(combined.getTime())
        ? ''
        : combined.toISOString()
      setValue(nextIso)
      clearError()
      field.onChange?.(nextIso, formContext)
    } else if (iso) {
      // A half was cleared after a full value existed - drop the stored value
      // so validation treats the field as empty again.
      setValue('')
    }
  }

  return (
    <FieldShell field={field} error={touched ? error : undefined}>
      <div className="grid grid-cols-2 gap-2">
        <DatePicker
          data-testid={testId}
          value={datePart}
          onChange={(next) => commit(next, timePart)}
          placeholder={field.placeholder}
          className={field.inputClassName}
          disablePast={field.disablePast}
          disableFuture={field.disableFuture}
        />
        <TimeSelect
          value={timePart}
          onChange={(next) => commit(datePart, next)}
          step={15}
          ariaLabel={label ? `${label} time` : 'Time'}
        />
      </div>
    </FieldShell>
  )
}

function DateRangeField({ field }: { field: FormField }) {
  const name = getFieldName(field)
  const formContext = useFormContext()
  const { error, touched } = useFieldState(name)
  const value = (formContext.values[name] as { from?: string; to?: string }) ?? {}

  return (
    <FieldShell field={{ ...field, className: cn('col-span-full', field.className) }} error={touched ? error : undefined}>
      <div className="grid grid-cols-2 gap-4">
        <DatePicker
          value={value.from ?? ''}
          onChange={(nextValue) => {
            formContext.setFieldValue(`${name}.from`, nextValue)
            formContext.setFieldError(name, undefined)
            field.onChange?.({ ...value, from: nextValue }, formContext)
          }}
          placeholder="Start date"
        />
        <DatePicker
          value={value.to ?? ''}
          onChange={(nextValue) => {
            formContext.setFieldValue(`${name}.to`, nextValue)
            formContext.setFieldError(name, undefined)
            field.onChange?.({ ...value, to: nextValue }, formContext)
          }}
          placeholder="End date"
        />
      </div>
    </FieldShell>
  )
}

function CurrencyField({ field }: { field: FormField }) {
  const name = getFieldName(field)
  const formContext = useFormContext()
  const { value: raw, error, touched, setValue, clearError } = useFieldState(name)
  const value = (raw as { amount?: number; currency?: string } | undefined) ?? {}

  return (
    <FieldShell field={field} error={touched ? error : undefined}>
      <CurrencyInput
        value={value}
        currencyOptions={field.options}
        onChange={(next) => {
          setValue(next)
          clearError()
          field.onChange?.(next, formContext)
        }}
      />
    </FieldShell>
  )
}

/**
 * Resolve a unit value's human label from the UOM option config - searching
 * every cascade group and the flat fallback options - so a stored unit renders
 * with its catalogue label even when the cascade parent isn't in scope to
 * resolve its group. Falls back to a capitalised value.
 */
function uomOptionLabel(
  uomConfig: Record<string, unknown>,
  value: string,
): string {
  const groups = uomConfig.option_groups as
    | Record<string, { value: string; label: string }[]>
    | undefined
  if (groups) {
    for (const list of Object.values(groups)) {
      const hit = list?.find((opt) => opt.value === value)
      if (hit) return hit.label
    }
  }
  const flat = uomConfig.options as { value: string; label: string }[] | undefined
  const flatHit = flat?.find((opt) => opt.value === value)
  if (flatHit) return flatHit.label
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : value
}

/**
 * Composite "quantity + unit-of-measurement" input: a small UOM `Select`
 * (minor control, left) paired with the quantity `number` input (major
 * control, right) in one row - the same left-minor / right-major layout the
 * phone and currency inputs use.
 *
 * Presentation-only pairing: `quantity` and its unit stay two independent
 * attributes/form fields (so anything reading them separately keeps working);
 * this widget just renders them together and writes both. It is opt-in and
 * fully config-driven (no tenant branch): a NUMBER field activates it by
 * declaring `configuration_data.uom_field` (the companion field to write the
 * unit into) plus `configuration_data.uom` (the unit's option config, which
 * may itself cascade via `depends_on` / `option_groups`, exactly like a
 * SELECT). The companion field is hidden from standalone rendering by its own
 * `configuration_data.rendered_inline_by`. Tenants that do not seed these keys
 * keep the plain number input.
 */
function QuantityUomField({ field }: { field: FormField }) {
  const numName = getFieldName(field)
  const uomName = field.configurationData?.uom_field as string
  const uomConfig =
    (field.configurationData?.uom as Record<string, unknown> | undefined) ?? {}
  const formContext = useFormContext()
  const qty = useFieldState(numName)
  const uom = useFieldState(uomName)

  // Resolve the unit options against live values every render so a cascade
  // parent change (e.g. product type) re-cascades - reuses the SELECT engine.
  const resolved = resolveDependentOptions(uomConfig, formContext.values) ?? []
  const dependsOn = uomConfig.depends_on as string | undefined
  const parentValue = dependsOn
    ? (formContext.values[dependsOn] as unknown)
    : undefined
  const uomValue = uom.value
  // Always keep the stored unit visible with its catalogue label, even when the
  // cascade parent is not in this form's scope (e.g. the claim-detail edit card
  // has no ``product_impacted``, so the cascade would otherwise fall back to the
  // base options and hide - then clear - a valid saved unit). Mirrors the
  // currency widget appending a stored currency that is no longer offered.
  const options =
    uomValue && !resolved.some((opt) => opt.value === uomValue)
      ? [
          ...resolved,
          { value: uomValue as string, label: uomOptionLabel(uomConfig, uomValue as string) },
        ]
      : resolved

  // Drop a stale unit only when the cascade parent IS present and no longer
  // admits it (a real product-type change) - never when the parent is absent,
  // which would silently wipe a saved unit on a surface that doesn't edit it.
  useEffect(() => {
    if (!dependsOn || !parentValue) return
    if (uomValue == null || uomValue === '') return
    if (!resolved.some((opt) => opt.value === uomValue)) uom.setValue('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependsOn, parentValue, resolved, uomValue])

  const qtyError = qty.touched ? qty.error : undefined
  const uomError = uom.touched ? uom.error : undefined
  const uomPlaceholder = (uomConfig.placeholder as string) || 'Unit'

  return (
    <FieldShell field={field} error={qtyError || uomError}>
      <div className="flex items-stretch gap-2">
        <div className="w-[130px] shrink-0">
          <Select
            value={(uom.value as string) ?? ''}
            onValueChange={(next) => {
              uom.setValue(next)
              uom.clearError()
            }}
          >
            <SelectTrigger
              aria-label={uomPlaceholder}
              className={cn(
                'w-full',
                uomError && 'ring-1 ring-[var(--status-error)]',
              )}
            >
              <SelectValue placeholder={uomPlaceholder} />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Input
          id={numName}
          type="number"
          inputMode={field.inputMode ?? 'decimal'}
          className="flex-1"
          value={(qty.value as string | number) ?? ''}
          placeholder={field.placeholder}
          disabled={field.disabled}
          aria-invalid={Boolean(qtyError) || undefined}
          onChange={(event) => {
            qty.setValue(event.target.value)
            qty.clearError()
            field.onChange?.(event.target.value, formContext)
          }}
          onBlur={(event) => {
            qty.markTouched()
            field.onBlur?.(event.target.value, formContext)
          }}
        />
      </div>
    </FieldShell>
  )
}

function resolveFileName(value: File | Record<string, unknown> | null): string | undefined {
  if (!value) return undefined
  if (value instanceof File) return value.name
  if (typeof value.display === 'string' && value.display) return value.display
  if (typeof value.file_name === 'string') return value.file_name
  if (typeof value.fileName === 'string') return value.fileName
  const nested = value.value as Record<string, unknown> | undefined
  if (nested && typeof nested.file_name === 'string') return nested.file_name
  return undefined
}

/**
 * "This is what to photograph" images the backend resolved for this field.
 *
 * Stamped onto `configuration_data` server-side from the tenant's
 * evidence-request template, so the claim form shows the same examples as the
 * evidence-upload page without the tenant re-uploading them.
 */
function exampleMediaUrls(field: FormField): string[] {
  const raw = field.configurationData?.example_media_urls
  if (!Array.isArray(raw)) return []
  return raw.filter((url): url is string => typeof url === 'string' && !!url)
}

/**
 * FILE / IMAGE field renderer. Single-file mode keeps the bespoke inline
 * dropzone (compression, example gallery, upload-on-submit retry). Multiple
 * mode - driven purely by ``configuration_data.file_multiple`` - delegates to
 * the shared multi-file ``FileUploader`` (the same component the evidence
 * modal and admin claim wizard use), so document/photo evidence can hold more
 * than one file. ``field.multiple`` is stable per field identity, so the
 * branch never flips between renders.
 */
function FileField({ field, picture = false }: { field: FormField; picture?: boolean }) {
  if (field.multiple) return <MultiFileField field={field} picture={picture} />
  return <SingleFileField field={field} picture={picture} />
}

/**
 * Multi-file evidence via the shared {@link FileUploader}. Stores an array of
 * upload envelopes (`{uuid, file_name, file_url}`) in the form value - the
 * backend's file-attribute handling coerces a list of envelopes to a list of
 * UUIDs, so no submit-time reshaping is required. Image compression (incl.
 * HEIC -> JPEG) is folded into the uploader's ``uploadFn`` so multi-upload
 * keeps the same size/format guarantees as the single-file path.
 */
function MultiFileField({ field, picture = false }: { field: FormField; picture?: boolean }) {
  const name = getFieldName(field)
  const formContext = useFormContext()
  const { value: raw, error, touched, setValue, clearError } = useFieldState(name)
  const uploadFile = useUpload()
  const examples = exampleMediaUrls(field)

  const accept = picture ? 'image/*' : getAcceptForUseCase(field.useCase)
  const acceptLabel = humanizeAccept(accept)

  const items: UploadedFileItem[] = Array.isArray(raw)
    ? (raw as unknown[]).filter(isUploadedFileValue)
    : []

  const uploadFn = async (file: File): Promise<UploadedFileItem> => {
    if (!uploadFile) throw new Error('No file upload handler configured')
    let f = file
    try {
      f = await compressImage(file)
    } catch {
      f = file
    }
    const useCase = field.useCase || 'PROOF_OF_PURCHASE'
    const r = await uploadFile({
      file: f,
      use_case: useCase,
      fieldName: name,
    })
    // Single source of truth for the envelope shape - reconciles the upload
    // hook's inconsistent key casing (see toUploadedFileItem).
    return toUploadedFileItem(r, { name: f.name, type: f.type })
  }

  const handleChange = (files: UploadedFileItem[]) => {
    setValue(
      files.map((it) => ({
        uuid: it.uuid,
        file_name: it.file_name,
        file_url: it.file_url ?? '',
      })),
    )
    clearError()
    field.onChange?.(files, formContext)
  }

  return (
    <FieldShell field={field} error={touched ? error : undefined}>
      <ExampleGallery urls={examples} label={field.label ?? name} />
      <FileUploader
        value={items}
        onChange={handleChange}
        uploadFn={uploadFn}
        multiple
        accept={accept}
        acceptLabel={acceptLabel || undefined}
        hint={field.hint || undefined}
        error={touched ? !!error : false}
        disabled={!uploadFile}
      />
    </FieldShell>
  )
}

/** Narrow an unknown form value to a committed upload envelope. */
function isUploadedFileValue(v: unknown): v is UploadedFileItem {
  return (
    !!v &&
    typeof v === 'object' &&
    typeof (v as { uuid?: unknown }).uuid === 'string' &&
    !!(v as { uuid?: string }).uuid
  )
}

function SingleFileField({ field, picture = false }: { field: FormField; picture?: boolean }) {
  const name = getFieldName(field)
  const formContext = useFormContext()
  const { value: raw, error, touched, setValue, clearError } = useFieldState(name)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const value = raw as File | Record<string, unknown> | null
  const fileName = resolveFileName(value)
  const examples = exampleMediaUrls(field)
  const [dragActive, setDragActive] = useState(false)
  const [uploading, setUploading] = useState(false)
  const uploadIdRef = useRef(0)
  const uploadFile = useUpload()

  const acceptFile = async (nextFile: File | null) => {
    const myId = ++uploadIdRef.current

    if (!nextFile) {
      setUploading(false)
      setValue(null)
      field.onChange?.(null, formContext)
      return
    }

    // Shrink + re-encode photos (incl. iPhone HEIC -> JPEG) before storing or
    // uploading, so a normal phone photo does not exceed the upload size cap
    // or arrive in an unsupported format. compressImage self-gates: it returns
    // the original untouched for non-images and already-small files.
    setUploading(true)
    let file = nextFile
    try {
      file = await compressImage(nextFile)
    } catch {
      file = nextFile
    }
    if (uploadIdRef.current !== myId) return

    if (uploadFile) {
      setValue(file)
      clearError()
      setUploading(true)
      const useCase = field.useCase || 'PROOF_OF_PURCHASE'
      uploadFile({ file, use_case: useCase, fieldName: name })
        .then((result) => {
          if (uploadIdRef.current !== myId) return
          setValue(result)
          field.onChange?.(result, formContext)
        })
        .catch(() => {
          if (uploadIdRef.current !== myId) return
          formContext.setFieldError(name, 'Upload failed - will retry on submit')
        })
        .finally(() => {
          if (uploadIdRef.current !== myId) return
          setUploading(false)
        })
    } else {
      setUploading(false)
      setValue(file)
      clearError()
      field.onChange?.(file, formContext)
    }
  }

  const handleRemove = () => {
    uploadIdRef.current++
    setUploading(false)
    setValue(null)
    field.onFileDelete?.()
  }

  return (
    <FieldShell field={field} error={touched ? error : undefined}>
      <ExampleGallery urls={examples} label={field.label ?? name} />
      {fileName ? (
        <div className="flex items-center gap-3 rounded-[8px] border border-input bg-[var(--bg-subtle)] p-3">
          {uploading ? (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[var(--green)]" />
          ) : (
            <Upload className="h-4 w-4 shrink-0 text-[var(--green)]" />
          )}
          <span className="flex-1 truncate text-[13px] text-[var(--ink)]">
            {uploading ? `Uploading ${fileName}...` : fileName}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            onClick={handleRemove}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          className={cn(
            'h-auto w-full justify-center border-dashed py-6 transition-colors',
            dragActive
              ? 'border-[var(--green)] bg-[var(--green-bg)] text-[var(--green)]'
              : 'text-[var(--ink-3)]',
          )}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault()
            if (!dragActive) setDragActive(true)
          }}
          onDragLeave={(e) => {
            e.preventDefault()
            setDragActive(false)
          }}
          onDrop={(e) => {
            e.preventDefault()
            setDragActive(false)
            const dropped = e.dataTransfer.files?.[0] ?? null
            if (dropped) acceptFile(dropped)
          }}
        >
          <div className="flex flex-col items-center gap-2">
            <Upload className="h-5 w-5 opacity-60" />
            <span className="text-[13px]">
              {dragActive
                ? 'Drop file to upload'
                : field.hint ||
                  (picture
                    ? 'Click or drag an image here'
                    : 'Click or drag a file here')}
            </span>
          </div>
        </Button>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept={picture ? 'image/*' : getAcceptForUseCase(field.useCase)}
        className="hidden"
        onChange={(event) => {
          const nextFile = event.target.files?.[0] ?? null
          acceptFile(nextFile)
          event.currentTarget.value = ''
        }}
      />
    </FieldShell>
  )
}

function TextareaField({
  field,
  autoCapitalize = false,
}: {
  field: FormField
  autoCapitalize?: boolean
}) {
  const name = getFieldName(field)
  const testId = getFieldTestId(field)
  const formContext = useFormContext()
  const { value: raw, error, touched, setValue, clearError } = useFieldState(name)
  const value = (raw as string) ?? ''
  const hasError = touched && Boolean(error)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const cursorRestoreRef = useRef<number | null>(null)
  const shouldCapitalize = autoCapitalize && field.autoCapitalize !== false

  // Restore the caret after capitalisation rewrites the first character so
  // the cursor doesn't jump to the end mid-edit. Mirrors CurrencyInput.
  useEffect(() => {
    if (cursorRestoreRef.current !== null && textareaRef.current) {
      const pos = cursorRestoreRef.current
      textareaRef.current.setSelectionRange(pos, pos)
      cursorRestoreRef.current = null
    }
  })
  const errorMessage = hasError ? error : undefined
  const showCounter = Boolean(field.maxLength)

  const footer = showCounter ? (
    <div className="mt-1 flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        {errorMessage ? (
          <p className="text-[11px] leading-[1.45] text-[var(--status-error)]">{errorMessage}</p>
        ) : field.hint ? (
          <p className="text-[11px] leading-[1.45] text-[var(--ink-3)]">{field.hint}</p>
        ) : null}
      </div>
      <p className="shrink-0 pt-0.5 text-[10px] tracking-[0.04em] text-[var(--ink-3)] tabular-nums">
        {value.length}/{field.maxLength}
      </p>
    </div>
  ) : undefined

  return (
    <FieldShell field={field} error={errorMessage} footer={footer}>
      <Textarea
        ref={textareaRef}
        data-testid={testId}
        id={name}
        rows={4}
        value={value}
        placeholder={field.placeholder}
        disabled={field.disabled}
        maxLength={field.maxLength}
        aria-invalid={hasError || undefined}
        className={field.inputClassName}
        onChange={(event) => {
          const rawValue = event.target.value
          const next = shouldCapitalize ? capitalizeFirstLetter(rawValue) : rawValue
          if (next !== rawValue) cursorRestoreRef.current = event.target.selectionStart
          setValue(next)
          clearError()
          field.onChange?.(next, formContext)
        }}
      />
    </FieldShell>
  )
}

function AddressField({ field }: { field: FormField }) {
  const name = getFieldName(field)
  const formContext = useFormContext()
  const { error, touched } = useFieldState(name)
  const current = toMailingAddress(formContext.values[name])

  const lookupProvider = field.configurationData?.lookup_provider as string | undefined
  const defaultCountry = field.configurationData?.default_country as string | undefined
  const requiredComponents = Array.isArray(field.configurationData?.required_components)
    ? (field.configurationData?.required_components as string[])
    : undefined

  // Persist the backend-compatible camelCase shape (unchanged); AddressCapture
  // is the shared UI. `toAddressInputValue(null)` yields an empty camelCase
  // object so the stored shape stays consistent when cleared.
  const handleChange = (next: MailingAddress | null) => {
    const stored = toAddressInputValue(next)
    formContext.setFieldValue(name, stored)
    formContext.setFieldError(name, undefined)
    field.onChange?.(stored, formContext)
  }

  // Reflect unresolved search text into the stored value as `pendingSearch` so
  // the address validation rejects a "typed but never selected / entered
  // manually" state instead of silently saving an empty address. Never clobber
  // committed fields: attach the marker on top of them, and only strip it (not
  // the address) once the term clears.
  const handlePendingChange = useCallback(
    (term: string) => {
      const base = toAddressInputValue(formContext.values[name])
      const hadPending =
        typeof (formContext.values[name] as { pendingSearch?: unknown } | undefined)
          ?.pendingSearch === 'string' &&
        !!(formContext.values[name] as { pendingSearch?: string }).pendingSearch
      if (term) {
        formContext.setFieldValue(name, { ...base, pendingSearch: term })
      } else if (hadPending) {
        formContext.setFieldValue(name, base)
      }
    },
    [formContext, name],
  )

  const addressError = touched ? error : undefined

  return (
    <FieldShell field={{ ...field, className: cn('col-span-2', field.className) }} error={addressError}>
      <AddressCapture
        value={isMeaningfulMailingAddress(current) ? current : null}
        onChange={handleChange}
        onPendingChange={handlePendingChange}
        lookupProvider={lookupProvider}
        defaultCountry={defaultCountry}
        requiredComponents={requiredComponents}
        summaryLabel=""
      />
    </FieldShell>
  )
}

/** First distinctive token of a product/resource name, for hint examples. */
function shortExampleName(name: string): string {
  return (name.split(/[\s\-\u2013\u2014®™,]+/).filter(Boolean)[0] ?? '').trim()
}

function ResourceSearchField({ field }: { field: FormField }) {
  const name = getFieldName(field)
  const formContext = useFormContext()
  const { value: raw, error, touched, setValue, clearError } = useFieldState(name)
  const providerKey = field.configurationData?.lookup_provider as string | undefined
  const iconName = (field.configurationData?.icon as string) ?? 'building-2'
  // Static params forwarded to the provider so it can scope results (e.g. the
  // component pickers pass `category_slug` so a Hip & Ridge field only searches
  // the Hip & Ridge catalog). Providers that take no params simply ignore it.
  const staticParams = field.configurationData?.provider_params as
    | Record<string, unknown>
    | undefined
  // Live cascade: when the field declares `depends_on` + `scope_param`, the
  // current value of the sibling field is merged into the provider params so
  // results narrow as that sibling changes (e.g. a "Product Impacted" select
  // scopes the product search to that category). `scope_map` optionally
  // translates the sibling value (e.g. `roofing` -> `shingle-lines`) before it
  // is sent; without a map the raw value is forwarded. Generic: only active
  // when both keys are present, so every other ENTITY_SEARCH field is
  // unaffected.
  const dependsOn = field.configurationData?.depends_on as string | undefined
  const scopeParam = field.configurationData?.scope_param as string | undefined
  const scopeMap = field.configurationData?.scope_map as
    | Record<string, string>
    | undefined
  const siblingValue = dependsOn ? formContext.values[dependsOn] : undefined
  const providerParams = useMemo(() => {
    if (!scopeParam || siblingValue == null || siblingValue === '') {
      return staticParams
    }
    // The sibling may itself be an ENTITY_SEARCH value ({ uuid, name }); use
    // its uuid so `String(...)` never collapses to "[object Object]".
    const raw =
      typeof siblingValue === 'object' &&
      siblingValue !== null &&
      'uuid' in siblingValue
        ? String((siblingValue as { uuid: unknown }).uuid)
        : String(siblingValue)
    const mapped = scopeMap ? scopeMap[raw] : raw
    // Sibling value not in the map -> no extra scoping (fall back to the full,
    // static-param list rather than an empty result set).
    if (!mapped) return staticParams
    return { ...(staticParams ?? {}), [scopeParam]: mapped }
  }, [scopeParam, scopeMap, siblingValue, staticParams])
  // When set, the picker lets the user add a not-yet-listed entry by name
  // (yields `{ uuid: '', name }` - a free-text, non-member selection).
  const allowCreate = Boolean(field.configurationData?.allow_create)
  const provider = useResourceLookup(providerKey)

  const [results, setResults] = useState<ResourceOption[]>([])
  const [examples, setExamples] = useState<string[]>([])
  // The scope (paramsKey) whose full, unfiltered list came back empty - e.g. a
  // product with no colours. Typing a filter that matches nothing never sets it.
  const [emptyScopeKey, setEmptyScopeKey] = useState<string | null>(null)

  // Fetch for any term length (including empty) so the picker can open as a
  // dropdown on focus and show the full scoped list, then narrow as the user
  // types. The provider scopes results via providerParams (e.g. category).
  const handleSearch = (term: string) => {
    if (!provider) {
      setResults([])
      return
    }
    provider
      .search(term, providerParams)
      .then(setResults)
      .catch(() => setResults([]))
  }

  // Pull a couple of real example names (scoped by providerParams) so the
  // empty-state hint can suggest what to type - data-driven, never hardcoded.
  const paramsKey = JSON.stringify(providerParams ?? null)
  useEffect(() => {
    if (!provider) return
    let cancelled = false
    provider
      .search('', providerParams)
      .then((opts) => {
        if (cancelled) return
        setResults(opts ?? [])
        setEmptyScopeKey((opts ?? []).length === 0 ? paramsKey : null)
        const names = (opts ?? [])
          .map((o) => shortExampleName(o.name))
          .filter((n) => n.length > 1)
        setExamples(Array.from(new Set(names)).slice(0, 2))
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, paramsKey])

  const value = raw as { uuid: string; name: string } | null

  // A pick scoped by its sibling (e.g. a colour of the chosen product) stops
  // being valid when that sibling changes, so clear it. Skips the first render
  // so a saved value survives the form opening.
  const scopeKey = scopeParam ? paramsKey : null
  const lastScopeKey = useRef(scopeKey)
  useEffect(() => {
    if (lastScopeKey.current === scopeKey) return
    lastScopeKey.current = scopeKey
    if (raw != null && raw !== '') setValue(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey])

  // WPS-270: when the sibling-scoped list is empty (a product with no colours),
  // record `configuration_data.empty_value` (e.g. "NA") instead of a blank that
  // reads as missing. Never over a value already chosen.
  const emptyValue = field.configurationData?.empty_value as string | undefined
  const scoped = Boolean(scopeParam && providerParams?.[scopeParam])
  useEffect(() => {
    if (!emptyValue || !scoped || emptyScopeKey !== paramsKey) return
    if (raw == null || raw === '') setValue({ uuid: '', name: emptyValue })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emptyValue, scoped, emptyScopeKey, paramsKey, raw])

  const handleChange = (next: { uuid: string; name: string } | null) => {
    setValue(next)
    clearError()
    field.onChange?.(next, formContext)
  }

  return (
    <FieldShell field={field} error={touched ? error : undefined}>
      <ResourceSearchInput
        value={value}
        onChange={handleChange}
        results={results}
        onSearch={handleSearch}
        examples={examples}
        iconName={iconName}
        searchPlaceholder={field.placeholder}
        emptyMessage={
          (field.configurationData?.empty_message as string | undefined) ??
          field.hint ??
          undefined
        }
        allowCreate={allowCreate}
      />
    </FieldShell>
  )
}

/**
 * A free-text field with a type-ahead over prior values (WPS-164 #15). The
 * suggestion list is served by a resource-lookup provider named by
 * `configuration_data.autocomplete_source` (e.g. `claim_attribute_history`),
 * scoped to this field's `code_name`; the stored value is the plain string the
 * user typed or picked, so free text is always allowed. Generic: any TEXT
 * attribute opts in via config, no field name is hardcoded here.
 */
function SearchAutocompleteField({ field }: { field: FormField }) {
  const name = getFieldName(field)
  const testId = getFieldTestId(field)
  const formContext = useFormContext()
  const { value, error, touched, setValue, clearError, markTouched } = useFieldState(name)
  const source = field.configurationData?.autocomplete_source as string | undefined
  const provider = useResourceLookup(source)
  const [suggestions, setSuggestions] = useState<string[]>([])
  const hasError = touched && Boolean(error)
  const current = (value as string) ?? ''
  const listId = `${name}-autocomplete`

  // Debounced fetch: the provider searches the attribute's history, scoped to
  // this field's code_name. A missing provider simply yields no suggestions -
  // the field still works as plain text.
  useEffect(() => {
    if (!provider) return
    let cancelled = false
    const handle = setTimeout(() => {
      provider
        .search(current, { code_name: name })
        .then((opts) => {
          if (cancelled) return
          const names = (opts ?? [])
            .map((o) => o.name)
            .filter((n): n is string => typeof n === 'string' && n.length > 0)
          setSuggestions(Array.from(new Set(names)).slice(0, 20))
        })
        .catch(() => {
          if (!cancelled) setSuggestions([])
        })
    }, 200)
    return () => {
      cancelled = true
      clearTimeout(handle)
    }
  }, [provider, current, name])

  return (
    <FieldShell field={field} error={hasError ? error : undefined}>
      <Input
        data-testid={testId}
        id={name}
        type="text"
        list={listId}
        value={current}
        placeholder={field.placeholder}
        disabled={field.disabled}
        autoFocus={field.autoFocus}
        aria-invalid={hasError || undefined}
        onChange={(event) => {
          setValue(event.target.value)
          clearError()
          field.onChange?.(event.target.value, formContext)
        }}
        onBlur={() => markTouched()}
      />
      <datalist id={listId}>
        {suggestions.map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>
    </FieldShell>
  )
}

function IncrementalField({ field, context }: { field: FormField; context?: RenderInputContext }) {
  const fieldName = getFieldName(field)
  const methods = useReactHookFormContext<Record<string, unknown>>()
  const { fields, append, remove } = useFieldArray({ control: methods.control, name: fieldName })
  const subFields = field.fields ?? []
  const createEmptyItem = () =>
    subFields.reduce<Record<string, unknown>>((acc, subField) => {
      acc[getFieldName(subField)] = subField.value || ''
      return acc
    }, {})

  return (
    <div className={cn('col-span-full flex flex-col', field.className)}>
      <Label className="mb-2 block clara-label">{getFieldLabel(field)}</Label>
      <div className="flex flex-col gap-4">
        {fields.map((item, itemIndex) => (
          <div key={item.id} className="grid grid-cols-[1fr_auto] gap-4">
            <div className="grid grid-cols-2 gap-4">
              {subFields.map((subField, subFieldIndex) => (
                <GeneratedInput
                  key={`${item.id}-${getFieldName(subField)}`}
                  index={subFieldIndex}
                  context={context}
                  field={{
                    ...subField,
                    name: `${fieldName}.${itemIndex}.${getFieldName(subField)}`,
                  }}
                />
              ))}
            </div>
            <Button type="button" variant="destructive" size="icon" onClick={() => remove(itemIndex)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <Button type="button" onClick={() => append(createEmptyItem() as never)} className="w-fit">
          + Add {getFieldLabel(field)}
        </Button>
      </div>
    </div>
  )
}

export function GeneratedInput({ field: rawField, index, context = {} }: GeneratedInputProps) {
  const fallbackContext = useFormContext()
  const formContext = context.formContext ?? fallbackContext

  if (typeof rawField.visible === 'boolean' && !rawField.visible) {
    return null
  }

  if (typeof rawField.visible === 'function' && !rawField.visible(formContext)) {
    return null
  }

  if (
    rawField.configurationData?.visible_when &&
    !isFieldVisible(rawField.configurationData, formContext.values)
  ) {
    return null
  }

  // A field rendered inline by a composite sibling (e.g. the unit half of a
  // quantity+UOM pair) is not drawn on its own - the composite draws it. Its
  // value + validation still apply; the composite writes it and surfaces its
  // error.
  if (rawField.configurationData?.rendered_inline_by) {
    return null
  }

  const conditionallyRequired = isConditionallyRequired(rawField.configurationData, formContext.values)
  const effectiveRequired = rawField.required || conditionallyRequired
  const field =
    effectiveRequired && !rawField.required
      ? { ...rawField, required: 'This field is required' }
      : rawField
  const type = field.type

  if (type === 'custom') {
    return field.render ? (
      <div key={context.key || `field-${index}`} className={field.className}>
        {field.render(formContext, index, context)}
      </div>
    ) : null
  }

  if (type === 'incremental') {
    return <IncrementalField field={field} context={context} />
  }

  if (type === 'dateRange') {
    return <DateRangeField field={field} />
  }

  if (type === 'select' || type === 'multiSelect') {
    return <SelectField field={field} />
  }

  if (type === 'checkbox') {
    return <CheckboxField field={field} />
  }

  if (type === 'switch') {
    return <SwitchField field={field} />
  }

  if (type === 'disclaimer') {
    return <DisclaimerField field={field} />
  }

  if (type === 'datepicker') {
    return <DateField field={field} />
  }

  if (type === 'datetime') {
    return <DateTimeField field={field} />
  }

  if (type === 'currency') {
    return <CurrencyField field={field} />
  }

  if (type === 'file') {
    return <FileField field={field} />
  }

  if (type === 'picture') {
    return <FileField field={field} picture />
  }

  if (type === 'rich-text' || type === 'textarea') {
    // Auto-capitalise plain prose only; rich-text may hold markup.
    return <TextareaField field={field} autoCapitalize={type === 'textarea'} />
  }

  if (type === 'address') {
    return <AddressField field={field} />
  }

  if (type === 'entity-search') {
    return <ResourceSearchField field={field} />
  }

  if (type === 'email') {
    return <TextField field={field} inputType="email" />
  }

  if (type === 'password') {
    return <TextField field={field} inputType="password" />
  }

  if (type === 'number') {
    // Composite quantity + unit-of-measurement (config-driven, opt-in).
    if (field.configurationData?.uom_field) {
      return <QuantityUomField field={field} />
    }
    return <TextField field={field} inputType="number" />
  }

  if (type === 'phone') {
    return <PhoneField field={field} />
  }

  if (type === 'search-autocomplete') {
    return <SearchAutocompleteField field={field} />
  }

  // Plain free-text: auto-capitalise the first letter (SX-40).
  return <TextField field={field} autoCapitalize />
}
